import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { generateNanoId } from '../../../../../../../../utils/generateNanoId';
import {
  S3MultipartFile,
  S3MultipartUploadFile,
  useS3MultipartUpload
} from '../../../../../../../s3Multipart/hooks/useS3MultipartUpload';

export interface ReferenceFileItem {
  id: string;
  localUrl: string;
  url: string | null;
  isUploading: boolean;
}

interface UseReferenceFilesUploadResult {
  referenceFiles: ReferenceFileItem[];
  referenceFileUrls: string[];
  isUploadingReference: boolean;
  applyReferenceFiles: (files: File[]) => void;
  handleReferenceFilesChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveReference: (id: string) => void;
  handleClearReferences: () => void;
  restoreReferences: (urls: string[]) => void;
}

// Multi-file counterpart of useReferenceFileUpload — keeps a list of
// reference images, each uploading independently. Upload ids come from the
// S3 hook's onFilesAccepted, so late callbacks for removed items no-op.
function useReferenceFilesUpload(): UseReferenceFilesUploadResult {
  const [referenceFiles, setReferenceFiles] = useState<ReferenceFileItem[]>([]);

  const referenceFilesRef = useRef(referenceFiles);
  referenceFilesRef.current = referenceFiles;

  useEffect(() => {
    return () => {
      referenceFilesRef.current.forEach((item) =>
        URL.revokeObjectURL(item.localUrl)
      );
    };
  }, []);

  const handleFilesAccepted = useCallback((files: S3MultipartUploadFile[]) => {
    setReferenceFiles((prev) => [
      ...prev,
      ...files.map((file) => ({
        id: file.id,
        // Object URL created by useS3MultipartUpload for the accepted file
        localUrl: file.file,
        url: null,
        isUploading: true
      }))
    ]);
  }, []);

  const handleFileUploaded = useCallback(
    (
      id: string,
      _uploadedId: string,
      _updatedFile: string,
      file: S3MultipartFile
    ) => {
      const s3Url = file?.presignedUrl?.split('?')[0] ?? null;
      setReferenceFiles((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, url: s3Url, isUploading: false } : item
        )
      );
    },
    []
  );

  const handleFileFailed = useCallback((id: string) => {
    setReferenceFiles((prev) => {
      const failed = prev.find((item) => item.id === id);
      if (failed) URL.revokeObjectURL(failed.localUrl);
      return prev.filter((item) => item.id !== id);
    });
  }, []);

  const { onUpload: uploadReferences } = useS3MultipartUpload({
    type: 'imageCrops',
    onFilesAccepted: handleFilesAccepted,
    onFileUploaded: handleFileUploaded,
    onFileFailed: handleFileFailed
  });

  const applyReferenceFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      uploadReferences(files);
    },
    [uploadReferences]
  );

  const handleReferenceFilesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      applyReferenceFiles(Array.from(e.target.files ?? []));
      // Allow re-selecting the same file
      e.target.value = '';
    },
    [applyReferenceFiles]
  );

  const handleRemoveReference = useCallback((id: string) => {
    setReferenceFiles((prev) => {
      const removed = prev.find((item) => item.id === id);
      if (removed) URL.revokeObjectURL(removed.localUrl);
      return prev.filter((item) => item.id !== id);
    });
  }, []);

  const handleClearReferences = useCallback(() => {
    setReferenceFiles((prev) => {
      prev.forEach((item) => URL.revokeObjectURL(item.localUrl));
      return [];
    });
  }, []);

  // Rebuilds the list from already-uploaded S3 urls (Adjust flow). Restored
  // items display the remote url directly, so there is no object URL to own;
  // revoking it on remove/clear is a no-op.
  const restoreReferences = useCallback((urls: string[]) => {
    setReferenceFiles((prev) => {
      prev.forEach((item) => URL.revokeObjectURL(item.localUrl));
      return urls.map((url) => ({
        id: generateNanoId(),
        localUrl: url,
        url,
        isUploading: false
      }));
    });
  }, []);

  const referenceFileUrls = useMemo(
    () =>
      referenceFiles
        .map((item) => item.url)
        .filter((url): url is string => Boolean(url)),
    [referenceFiles]
  );

  const isUploadingReference = useMemo(
    () => referenceFiles.some((item) => item.isUploading),
    [referenceFiles]
  );

  return {
    referenceFiles,
    referenceFileUrls,
    isUploadingReference,
    applyReferenceFiles,
    handleReferenceFilesChange,
    handleRemoveReference,
    handleClearReferences,
    restoreReferences
  };
}

export default useReferenceFilesUpload;
